import React from 'react';

// Reusable "Program Duration" comparison grid used across all program pages.
const ProgramDurationSection = ({
  eyebrowColor = '#2563EB',
  eyebrowBg = '#EFF6FF',
  eyebrowBorder = '#BFDBFE',
  titlePlain = 'Program',
  titleHighlight = 'Duration',
  titleHighlightColor = '#2563EB',
  subtitle = '',
  durations = [],
}) => (
  <section style={{ background: 'linear-gradient(180deg,#F8FAFC 0%,#F1F5F9 100%)', padding: '80px 5%', borderTop: '1px solid #E2E8F0' }}>
    <style>{`
      .pds-grid { display:grid; grid-template-columns:1fr 1fr; gap:28px; margin-top:44px; }
      .pds-card { background:#fff; border-radius:20px; border:1px solid #E2E8F0; overflow:hidden; box-shadow:0 1px 3px rgba(0,0,0,0.04); transition:box-shadow 0.25s,transform 0.25s; }
      .pds-card:hover { box-shadow:0 16px 40px rgba(0,0,0,0.08); transform:translateY(-3px); }
      .pds-header { display:flex; align-items:center; justify-content:space-between; gap:10px; color:#fff; font-weight:800; font-size:17px; padding:18px 24px; }
      .pds-header-left { display:flex; align-items:center; gap:12px; }
      .pds-header-icon { width:38px; height:38px; border-radius:10px; background:rgba(255,255,255,0.18); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
      .pds-header-badge { font-size:11.5px; font-weight:700; background:rgba(255,255,255,0.18); padding:5px 12px; border-radius:999px; white-space:nowrap; }
      .pds-body { padding:26px 24px 22px; }
      .pds-phases { display:flex; align-items:stretch; gap:0; margin-bottom:22px; }
      .pds-phase { flex:1; min-width:0; padding:0 14px; }
      .pds-phase:not(:first-child) { border-left:1.5px dashed #E2E8F0; }
      .pds-phase-badge { width:26px; height:26px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:800; color:#fff; margin:0 auto 10px; }
      .pds-phase-name { font-size:13.5px; font-weight:800; color:#0F172A; text-align:center; margin-bottom:2px; line-height:1.3; min-height:35px; display:flex; align-items:center; justify-content:center; }
      .pds-phase-dur { font-size:11px; color:#94A3B8; font-weight:700; text-align:center; text-transform:uppercase; letter-spacing:0.04em; margin-bottom:12px; }
      .pds-phase-list { list-style:none; padding:0; margin:0; font-size:12px; color:#475569; line-height:2; }
      .pds-phase-list li { display:block; width:100%; padding-left:14px; position:relative; }
      .pds-phase-list li::before { content:''; position:absolute; left:0; top:9px; width:6px; height:6px; border-radius:50%; background:var(--accent); opacity:0.55; }
      .pds-footer { display:flex; gap:10px; flex-wrap:wrap; border-top:1px solid #F1F5F9; padding-top:18px; }
      .pds-deliverable { display:flex; align-items:center; gap:6px; font-size:12px; font-weight:700; padding:7px 14px; border-radius:999px; }
      @media(max-width:900px) { .pds-grid { grid-template-columns:1fr; } }
      @media(max-width:560px) {
        .pds-phases { flex-direction:column; gap:18px; }
        .pds-phase:not(:first-child) { border-left:none; border-top:1.5px dashed #E2E8F0; padding-top:14px; }
        .pds-phase-name { min-height:auto; text-align:left !important; justify-content:flex-start !important; }
        .pds-phase-dur { text-align:left !important; }
        .pds-phase-badge { margin:0 0 10px !important; }
      }
    `}</style>
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ display: 'inline-block', fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', color: eyebrowColor, background: eyebrowBg, border: `1px solid ${eyebrowBorder}`, padding: '4px 14px', borderRadius: 20, marginBottom: 14, textTransform: 'uppercase' }}>Program Structure</div>
        <h2 style={{ fontSize: 'clamp(22px,3vw,34px)', fontWeight: 900, color: '#0F172A', lineHeight: 1.2, letterSpacing: '-0.02em', margin: '0 0 8px' }}>{titlePlain} <span style={{ color: titleHighlightColor }}>{titleHighlight}</span></h2>
        {subtitle && <p style={{ fontSize: 14.5, color: '#64748B', margin: '0 0 36px' }}>{subtitle}</p>}
      </div>
      <div className="pds-grid">
        {durations.map(d => (
          <div className="pds-card" key={d.title} style={{ '--accent': d.color }}>
            <div className="pds-header" style={{ background: `linear-gradient(135deg, ${d.color}, ${d.colorDark})` }}>
              <div className="pds-header-left">
                <div className="pds-header-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                </div>
                {d.title}
              </div>
              <div className="pds-header-badge">{d.phases.length} Phases</div>
            </div>
            <div className="pds-body">
              <div className="pds-phases">
                {d.phases.map((p, i) => (
                  <div className="pds-phase" key={p.name}>
                    <div className="pds-phase-badge" style={{ background: d.color }}>{i + 1}</div>
                    <div className="pds-phase-name">{p.name}</div>
                    <div className="pds-phase-dur">{p.duration}</div>
                    <ul className="pds-phase-list">
                      {p.items.map(it => <li key={it}>{it}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
              <div className="pds-footer">
                {d.deliverables.map(del => (
                  <div className="pds-deliverable" key={del} style={{ background: d.bg, color: d.color }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    {del}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default ProgramDurationSection;
